import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Box,
  Typography,
  Tooltip,
  Button,
  Stack,
  Paper,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CopyIcon from '@mui/icons-material/ContentCopy';

import {
  buildCurlCommand,
  getConnectCurlRoute,
  shouldIncludeBearerAuth,
} from './utils';

export default function ConnectDialog({
  open,
  handleClose,
  type,
  id,
  endpoint,
}) {
  const [url_tooltip, setUrlTooltip] = useState('Copy');
  const [curl_tooltip, setCurlTooltip] = useState('Copy');

  const copyText = (value, setTooltip) => {
    if ('clipboard' in navigator) {
      navigator.clipboard
        .writeText(value)
        .then(() => setTooltip('Copied!'), () => setTooltip('Copy Error!'));
    } else {
      setTooltip('Copy Error!');
    }
  };

  if (endpoint) {
    const curl_route = getConnectCurlRoute(endpoint);
    const curl_command = curl_route
      ? buildCurlCommand(
          endpoint.url,
          curl_route,
          shouldIncludeBearerAuth(endpoint),
        )
      : null;

    return (
      <Dialog onClose={handleClose} open={open} maxWidth='md' fullWidth>
        <DialogTitle>
          <Grid
            container
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid size='grow'>Connect</Grid>
            <Grid size={1} sx={{ maxWidth: '25px' }}>
              <IconButton aria-label='close' onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box padding='10px'>
            <Typography variant='h6' gutterBottom>
              Endpoint Address:
            </Typography>
            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              sx={{ mb: 3 }}
            >
              <Typography
                component='code'
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  flexGrow: 1,
                }}
              >
                {endpoint.url}
              </Typography>
              <Tooltip title={url_tooltip}>
                <IconButton
                  aria-label='copy endpoint address'
                  onClick={() => copyText(endpoint.url, setUrlTooltip)}
                  size='small'
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            {curl_command ? (
              <>
                <Typography variant='h6' gutterBottom>
                  Example Usage:
                </Typography>
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Paper
                    sx={{
                      padding: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      fontFamily: 'monospace',
                      flexGrow: 1,
                    }}
                  >
                    <Typography
                      component='code'
                      sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                      }}
                    >
                      {curl_command}
                    </Typography>
                  </Paper>
                  <Tooltip title={curl_tooltip}>
                    <IconButton
                      aria-label='copy curl example'
                      onClick={() => copyText(curl_command, setCurlTooltip)}
                      size='small'
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </>
            ) : (
              <Link href={endpoint.url} target='_blank' rel='noreferrer'>
                Open endpoint
              </Link>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  } else {
    const copy_text = `${import.meta.env.VITE_CLI_COMMAND} ${type} connect ${id}`;
    return (
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>
          <Grid
            container
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid size='grow'>Connect to {type}</Grid>
            <Grid size={1} sx={{ maxWidth: '25px' }}>
              <IconButton aria-label='close' onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box padding='10px'>
            <Typography paragraph>
              1. Install and configure the{' '}
              <Link href={`${import.meta.env.VITE_CLI_GITHUB}`} target='_blank'>
                {import.meta.env.VITE_ORG_NAME} python module
              </Link>
            </Typography>
            <Typography paragraph>2. Run the following command:</Typography>
            <Stack direction='row' spacing={2} alignItems='center'>
              <Typography
                component='code'
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  flexGrow: 1,
                }}
              >
                {copy_text}
              </Typography>
              <Tooltip title={url_tooltip}>
                <IconButton
                  aria-label='copy'
                  onClick={() => copyText(copy_text, setUrlTooltip)}
                  size='small'
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
}
